import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import jamespot, {
    DatasourceResultSort,
    DatasourceType,
    Format,
    WidgetDatasourceTableContent,
    WidgetDatasourceValues,
    WidgetDisplayMode,
    WidgetsName,
    WidgetTableColumn,
} from 'jamespot-user-api';
import styled from 'styled-components';
import { Widget, WidgetEditor } from 'jamespot-front-business';
import { getVisibleColumns } from './utils';
import { FitType, useArticleRT } from '../hooks';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch } from '../../../redux/store';

const JRCButton = commonComponentsRegistry.getLazyComponent('Button');
const WidgetTable = commonComponentsRegistry.getLazyComponent('WidgetTable');
const Loader = commonComponentsRegistry.getLazyComponent('Loader');

export const Wrapper = styled.div``;

export const AbsoluteWrapper = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${(props) => props.theme.space.md}px;
`;

export const WidgetDatasourceTableWrapper = ({
    uri,
    limit,
    tableHeadColor,
    tableHeadTextColor,
    tableHeadIconColor,
    tableRowColor,
    tableRowTextColor,
    tableColumnsData = [],
    tableBorderRadius,
    tableSizedColumns,
    tableSizedColumnsWidth,
    mode,
}: WidgetDatasourceTableContent & { uniqid: string; mode: WidgetDisplayMode }) => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [datasourceData, setDatasourceData] = useState<Record<string, WidgetDatasourceValues>[]>([]);
    const [datasourceModel, setDatasourceModel] = useState<WidgetTableColumn[]>([]);
    const [datasourceSort, setDatasourceSort] = useState<DatasourceResultSort>({ order: 'ascending', name: 'unknown' });
    const [datasourceType, setDatasourceType] = useState<DatasourceType | undefined>(undefined);
    const [datasourceIdentifier, setDatasourceIdentifier] = useState<string>('');

    const definition = useMemo(() => Widget.definition(WidgetsName.DatasourceTable), []);
    const rtConditions = useMemo<FitType[]>(() => {
        if (datasourceType === 'studio') {
            return [{ type: [datasourceIdentifier] }];
        }

        return [];
    }, [datasourceType, datasourceIdentifier]);
    const rtAudience = useMemo<string[]>(() => {
        if (datasourceType === 'doc') {
            return [datasourceIdentifier];
        }

        return [];
    }, [datasourceType, datasourceIdentifier]);

    const { rtLastObject } = useArticleRT(rtConditions, rtAudience);

    const fetchDatasource = useCallback(() => {
        if (!uri) {
            return;
        }

        setIsLoading(true);
        jamespot.datasource
            .get(uri, Format.VIEW)
            .then((response) => {
                setDatasourceModel(response.result.model);
                setDatasourceData(response.result.data);
                setDatasourceSort(response.result.sort);
                setDatasourceType(response.result.datasource.datasourceType);
                setDatasourceIdentifier(response.result.datasource.datasourceIdentifier);
            })
            .finally(() => setIsLoading(false));
    }, [uri]);

    useEffect(() => {
        if (uri !== undefined && tableColumnsData.length !== 0 && datasourceModel.length === 0) {
            fetchDatasource();
        }
    }, [uri, tableColumnsData, datasourceModel, fetchDatasource]);

    useEffect(() => {
        if (!rtLastObject) {
            return;
        }

        fetchDatasource();
    }, [rtLastObject, fetchDatasource]);

    useEffect(() => {
        if (!uri) {
            setDatasourceModel([]);
        }
    }, [uri]);

    useEffect(() => {
        if (mode === 'view' || mode === 'preview') {
            dispatch(WidgetEditor.slice.actions.flushAllEditor());
        }
    }, [mode, dispatch]);

    const columns = useMemo(
        () => getVisibleColumns(tableColumnsData, datasourceModel),
        [tableColumnsData, datasourceModel],
    );

    if (tableColumnsData.length === 0 && !isLoading && mode === 'edit') {
        return (
            <>
                <AbsoluteWrapper>
                    <img src={`/img/fast-intranet/${WidgetsName.DatasourceTable}.png`} alt="" />
                    <FormattedMessage id={definition.label} />
                    <JRCButton label="GLOBAL_Settings" />
                </AbsoluteWrapper>
            </>
        );
    }

    return isLoading ? (
        <AbsoluteWrapper>
            <Loader />
        </AbsoluteWrapper>
    ) : (
        <>
            {!isLoading && datasourceData.length === 0 && mode !== 'edit' ? (
                <AbsoluteWrapper>
                    <img src={`/img/fast-intranet/${WidgetsName.DatasourceTable}.png`} alt="" />
                    <FormattedMessage id={'WIDGET_Datasource_No_Results'} />
                </AbsoluteWrapper>
            ) : (
                <WidgetTable
                    rows={datasourceData}
                    columns={columns}
                    limit={limit}
                    tableHeadColor={tableHeadColor}
                    tableHeadTextColor={tableHeadTextColor}
                    tableHeadIconColor={tableHeadIconColor}
                    tableRowColor={tableRowColor}
                    tableRowTextColor={tableRowTextColor}
                    tableBorderRadius={tableBorderRadius}
                    tableColumnsData={tableColumnsData}
                    tableSizedColumns={tableSizedColumns}
                    tableSizedColumnsWidth={tableSizedColumnsWidth}
                    tableDefaultSortOrder={datasourceSort.order}
                    tableDefaultSortName={datasourceSort.name}
                />
            )}
        </>
    );
};
